/**
 * The right edge every thin timeline row shares: the time, then the ServiceTitan chip, each in its
 * own fixed-width column so both line up down the whole list. Both sit in the row's FIRST line box
 * (same height, same top), so a row whose middle text wraps never pushes either under it, and chip
 * tops line up with time tops on every row at every width.
 *
 * A narrow list (the call screen's story rail) cannot spare those columns: under `TIMELINE_NARROW` the
 * time leaves the right edge for the row's meta line (`RowMetaTime`, "· 2:07 pm") and an empty chip cell
 * takes no width, so the title and its details get the row. `ActivityTimeline` makes its list the
 * `timeline` size container; a row rendered outside one always keeps the wide columns.
 */

import type { ReactNode } from "react";

import { RecordLink } from "@/components/records/record-link";
import { SourceMark } from "@/components/records/source-mark";
import { T_META } from "@/components/records/visual-system";
import { cn } from "@/lib/utils";

/** The size container every timeline list carries, so its rows can tell how much room they have. */
export const TIMELINE_CONTAINER = "@container/timeline";

/** The narrow threshold, 20rem (342px at the app's 107% root), spelled as literal classes for Tailwind. Measured
 *  list widths: the call screen's story is 255-321px (narrow); the deal tray is 356px on a 390px phone and 664px on
 *  a desktop, Customer 360 369px and 639px (all wide). */
const TIMELINE_NARROW = {
  /** Shown only above the threshold: the trailing time column. */
  wideOnly: "@max-[20rem]/timeline:hidden",
  /** Shown only below it: the time in the meta line. */
  narrowOnly: "hidden @max-[20rem]/timeline:inline",
  /** Below it, a chip cell with no chip takes no width. */
  emptyChipGone: "@max-[20rem]/timeline:empty:hidden",
  /** A meta line that holds only the time: there only below it. */
  narrowOnlyLine: "hidden @max-[20rem]/timeline:flex",
} as const;

/** A meta line that exists only to carry the narrow-list time: drawn only in a narrow list. */
export const NARROW_ONLY_LINE = TIMELINE_NARROW.narrowOnlyLine;

/** The row's time inside its meta line ("· 2:07 pm"), drawn only in a narrow list, where `RowTrailing` drops
 *  its time column. Same tooltip and test id as that column. */
export function RowMetaTime({ time, timeTitle, className }: { time?: ReactNode; timeTitle?: string; className?: string }) {
  if (time === undefined || time === null || time === "") return null;
  return (
    <span
      className={cn(TIMELINE_NARROW.narrowOnly, "whitespace-nowrap tabular-nums", T_META, className)}
      title={timeTitle}
      data-testid="row-time"
      data-place="meta"
    >
      · {time}
    </span>
  );
}

/** The record chip for a ServiceTitan-recorded row. A record with a page: the shared `RecordLink`
 *  (the id lives only in its hover). A record with no page — a payment, a deposit invoice, a deleted
 *  estimate — the bare `SourceMark`, unlinked, with no id and no hover-copy: the reader can still tell
 *  ServiceTitan recorded it. No record at all: nothing. */
export function stRecordChip(record: { recordId: string; href: string | null } | null): ReactNode {
  if (!record) return null;
  if (record.href) {
    return <RecordLink link={{ label: "ST", href: record.href, id: record.recordId, system: "servicetitan" }} />;
  }
  return (
    <span role="img" aria-label="Recorded in ServiceTitan" className="inline-flex leading-none">
      <SourceMark system="servicetitan" />
    </span>
  );
}

export function RowTrailing({
  time,
  timeTitle,
  chip,
  before,
  lineClassName,
}: {
  time?: ReactNode;
  timeTitle?: string;
  chip?: ReactNode;
  /** Anything that rides ahead of the columns (the quiet "Belongs Here" move). */
  before?: ReactNode;
  /** The row's first-line height, so both cells share it exactly. */
  lineClassName: string;
}) {
  const cell = cn("flex shrink-0 items-center", lineClassName);
  return (
    <span className="flex shrink-0 items-start gap-2">
      {before ? <span className={cell}>{before}</span> : null}
      <span
        className={cn(cell, "w-[3.75rem] justify-end whitespace-nowrap tabular-nums", T_META, TIMELINE_NARROW.wideOnly)}
        title={timeTitle}
        data-testid="row-time"
        data-place="trailing"
      >
        {time}
      </span>
      {/* Always visible — never hover-only — at a quiet 50% opacity on a fine pointer, full on
          hover/focus; a touch device keeps it at full opacity always (John Lee, round 4, item 4:
          "Every ServiceTitan or Pipedrive record on the page is reachable through its source mark").
          Was hover-only (`pointer-fine:opacity-0`) through round 3c. */}
      <span
        className={cn(
          cell,
          "w-8 justify-start text-sm pointer-fine:opacity-50 pointer-fine:group-hover/row:opacity-100 pointer-fine:group-focus-within/row:opacity-100",
          TIMELINE_NARROW.emptyChipGone,
        )}
        data-testid="row-chip"
      >
        {chip}
      </span>
    </span>
  );
}
